import axios from 'axios';
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Filters, Card, Loading, Page, SkeletonPage, DataTable, Stack, Toast, Button } from '@shopify/polaris';
import Banners from '../Shared/Banners';
import useDebounce from '../../hooks/useDebounce';
import { stateConverter } from '../FilterEngine/filterParams';
import { appliedFiltersQuery, handleFilterChange } from '../FilterEngine/FilterEngine';
import { defaultSortDirection, initialSortColumnIndex, handleSort } from '../FilterEngine/Sort/sort';
import TablePagination from '../FilterEngine/TablePagination/TablePagination';
import PaymentOptions from '../Shared/PaymentOptions';

const Payments = () => {
  const [banner, setBanner] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [totalPayments, setTotalInvoices] = useState(0);
  const [lastPage, setLastPage] = useState(null);
  const [mounted, setMounted] = useState(true);

  const { t } = useTranslation();
  const history = useNavigate();
  const location = useLocation();

  const [tableFilters, setTableFilters] = useState(stateConverter(location));
  const debouncedSearch = useDebounce(tableFilters, 400);

  const [toastActive, setToastActive] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const toggleToastActive = useCallback((message) => {
    setToastActive(!toastActive);
    setToastMessage(message);
  }, [toastActive]);

  const toastMarkup = toastActive ? (
    <Toast content={toastMessage} onDismiss={toggleToastActive} duration={2000} />
  ) : null;

  const translateStatus = useCallback((status) => {
    switch (status) {
      case 'paid': return t('payments.paid');
      case 'processing': return t('payments.processing');
      case 'failed': return t('payments.failed');
      default: return '';
    }
  }, [t]);

  const downloadInvoice = (id) => {
    axios.get(`/v1/invoices/${id}/download`)
      .then((response) => {
        window.open(response.data.url);
      })
      .catch(() => {});
  };

  const modifier = useCallback((tableData) => tableData.map((row) => {
    const modifiedRow = [];

    modifiedRow.push(<Button plain onClick={() => downloadInvoice(row.invoice.token)}>{row.invoice.number}</Button>);
    modifiedRow.push(row.coach);
    modifiedRow.push(moment(row.processedAt).format('ll'));
    modifiedRow.push(translateStatus(row.status));
    modifiedRow.push(PaymentOptions(t).find((option) => option.value === row.paymentMethod)?.label);
    modifiedRow.push(row.amount);

    return modifiedRow;
  }), [translateStatus, t]);

  const appliedFilters = useCallback(() => {
    const af = [];

    return af;
  }, []);

  const fetchPayments = useCallback(() => {
    const params = {
      per_page: 30,
      page: tableFilters.page,
      search: tableFilters.queryValue,
      q: appliedFiltersQuery(appliedFilters()),
      s: { sorts: tableFilters.sorts }
    };

    axios.post('/v1/payments/search', params)
      .then((response) => {
        if (mounted) {
          const tableData = modifier(response.data.payments);
          setPayments(tableData);
          setLastPage(response.data.lastPage);
          setTotalInvoices(response.data.paymentsCount);
          setLoading(false);
        }
      })
      .catch((err) => {
        setBanner([{ title: t('shared.something_went_wrong'), status: 'critical', details: err.response?.data?.errors }]);
        setLoading(false);
      });
    // eslint-disable-next-line
  }, [debouncedSearch]);

  const filters = [];

  const placeHolderText = t('user.search');

  const filterControl = (
    <Filters
      queryValue={tableFilters.queryValue}
      filters={filters}
      onQueryChange={handleFilterChange({ history, location }, setTableFilters, 'queryValue')}
      onQueryClear={handleFilterChange({ history, location }, setTableFilters, 'queryValue', 'reset', '')}
      onClearAll={handleFilterChange({ history, location }, setTableFilters, '/payments', 'resetAll')}
      appliedFilters={appliedFilters()}
      queryPlaceholder={placeHolderText}
    />
  );

  useEffect(() => {
    setMounted(true);
    fetchPayments();
    return () => {
      setMounted(false);
    };
  }, [fetchPayments]);

  const dismissBanner = () => {
    setBanner([]);
  };

  const indexSortMapping = {
    invoice_id: true,
    invoice_user_name: true,
    processed_at: true
  };

  const headings = [
    t('payments.reference'),
    t('user.instructor'),
    t('payments.payment_date'),
    t('user.status'),
    t('invoices.payment_method'),
    t('payments.amount')
  ];

  const content = (
    <DataTable
      columnContentTypes={[
        'text',
        'text',
        'text',
        'text',
        'text',
        'text'
      ]}
      verticalAlign="middle"
      headings={headings}
      rows={payments}
      sortable={Object.values(indexSortMapping)}
      defaultSortDirection={defaultSortDirection(tableFilters.sorts)}
      initialSortColumnIndex={initialSortColumnIndex(indexSortMapping, tableFilters.sorts)}
      onSort={handleSort({ location, history }, setTableFilters, indexSortMapping)}
      footerContent={`${payments.length} ${t('shared.of')} ${totalPayments}`}
    />
  );

  let loadingContainer = null;
  if (loading) {
    loadingContainer = <Loading />;
  }

  return (
    <Page
      title={t('payments.payments')}
      fullWidth
      separator
    >
      <Banners banners={banner} onDismissBanner={dismissBanner} />
      {toastMarkup}
      {loadingContainer}
      {!loading ? (
        <Card sectioned>
          <Card.Subsection>
            <Stack>
              <Stack.Item fill>
                {filterControl}
              </Stack.Item>
            </Stack>
          </Card.Subsection>
          <Card.Subsection>
            {content}
          </Card.Subsection>
        </Card>
      ) : <SkeletonPage />}
      <TablePagination
        pageFilter={tableFilters.page}
        setTableFilters={setTableFilters}
        records={payments}
        lastPage={lastPage}
      />
    </Page>
  );
};

const mapStateToProps = (state) => ({
  isAdmin: state.auth.admin,
  isInstructor: state.auth.instructor
});

export default connect(mapStateToProps)(Payments);
